// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { dollarStringSchema } from '@/utilities/currency';
import { z } from 'zod';

// A single aging bucket's outstanding-balance total and invoice count.
// Mirrors the backend InvoiceAgingBucketTotal response type.
const invoiceAgingBucketTotalSchema = z.object({
  amount: dollarStringSchema,
  count: z.number(),
});

// Per-currency 5-bucket aging breakdown. Each currency stacks
// independently; there is no cross-currency total. "current" covers
// invoices not yet past due. Keys match the bucket codes returned by the
// invoice listing endpoint so the cards and table share the same
// vocabulary.
const currencyInvoiceAgingSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  current: invoiceAgingBucketTotalSchema,
  '1-30': invoiceAgingBucketTotalSchema,
  '31-60': invoiceAgingBucketTotalSchema,
  '61-90': invoiceAgingBucketTotalSchema,
  '91+': invoiceAgingBucketTotalSchema,
});

// Global aging breakdown powering the A/R aging report's summary cards.
// Universe is OPEN + PARTIALLY_PAID invoices with balance > 0, bucketed
// per currency. Go emits null for an empty slice, so coalesce to [].
const invoiceAgingSummarySchema = z.object({
  byCurrency: z
    .array(currencyInvoiceAgingSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type InvoiceAgingSummary = z.infer<typeof invoiceAgingSummarySchema>;

const invoiceAgingSummaryResponseSchema = z.object({
  data: invoiceAgingSummarySchema,
  metadata: responseMetadataSchema,
});

export async function getInvoiceAgingSummary(): Promise<InvoiceAgingSummary> {
  const url = '/v1/invoice-summary/aging';
  const response = await api.get(url);
  const result = await parseResponse(
    invoiceAgingSummaryResponseSchema,
    response.data,
    { schemaName: 'InvoiceAgingSummary', endpoint: url }
  );
  return result.data;
}
